/**
 * Modifications.js
 * 
 * This module provides functions for modifying and extruding 3D geometries in the
 * SightFlight Designer application. It focuses on:
 * 
 * 1. Creating extruded 3D meshes from 2D shapes
 * 2. Modifying obstacle heights
 * 3. Adjusting obstacle offset boundaries
 * 
 * These functions handle the complex 3D geometry operations needed for
 * obstacles on roof segments, including proper elevation handling.
 */

import * as THREE from 'three'
import { POLYGONS, RENDER_LAYERS } from "../Global/variables";
import { addOffset, projectObstaclePointsToRoof } from './Shapes';
import { COLORS } from '../Global/materials';
import { refreshPanelOverlapHighlights } from '../Panels/ValidationVisuals';

export function setObstacleVisualColor(mesh, color) {
    if (!mesh) return;
    if (mesh.material?.color?.set) {
        mesh.material.color.set(color);
    }
    const edgeOutline = mesh.getObjectByName("obstacleBoundaryEdges");
    if (edgeOutline?.material?.color?.set) {
        edgeOutline.material.color.set(color);
    }
}

/**
 * Updates an obstacle's mesh/shape after its vertices (cords) have changed.
 * Rebuilds the extruded geometry + offset from newPoints.
 */
export function updateObstacleShape(segmentIndex, obstacleIndex, newPoints, options = {}) {
    const obstruction = POLYGONS[segmentIndex]?.obstructions?.[obstacleIndex];
    if (!obstruction) return;
    const showOffset = options?.showOffset !== false;
    const ownerSegment =
        typeof obstruction.parentSegment === "number" ? obstruction.parentSegment : segmentIndex;
    const projectedPoints = projectObstaclePointsToRoof(newPoints, ownerSegment);
    const nextPoints = projectedPoints.length ? projectedPoints : newPoints;

    const { type, height, offset, isFilled } = obstruction;

    if (!obstruction.mesh) return;

    const oldMesh = obstruction.mesh;

    // Clean up old offset child (if any)
    const prevOffset = oldMesh.getObjectByName("offset");
    if (prevOffset) {
        prevOffset.removeFromParent();
        prevOffset.geometry.dispose();
        prevOffset.material.dispose();
    }
    const prevOffsetConnectors = oldMesh.getObjectByName("offsetConnectors");
    if (prevOffsetConnectors) {
        prevOffsetConnectors.removeFromParent();
        if (prevOffsetConnectors.geometry) prevOffsetConnectors.geometry.dispose();
        if (prevOffsetConnectors.material) prevOffsetConnectors.material.dispose();
    }

    // Save parent BEFORE removing the mesh from the scene
    const parent = oldMesh.parent;

    // Remove old mesh from scene graph
    if (parent) {
        parent.remove(oldMesh);
    }
    if (oldMesh.geometry) oldMesh.geometry.dispose();
    if (oldMesh.material) oldMesh.material.dispose();

    // If there's no parent, this obstacle is no longer in the scene
    // (e.g., it was deleted); bail out safely instead of crashing.
    if (!parent) {
        return;
    }

    // Build new 2D shape from updated 3D points (XY projection)
    const shapePoints = nextPoints.map(p => new THREE.Vector2(p.x, p.y));
    const newShape = new THREE.Shape(shapePoints);

    // Re-extrude obstacle
    const color = obstruction.ogColor || COLORS.red;
    const extrudeMesh = addExtrude(nextPoints, newShape, height, color, parent, isFilled);

    // Recreate offset boundary with SAME offset & height
    const ret = addOffset(
        extrudeMesh,
        nextPoints,
        type,
        newShape,
        offset,
        height,
        segmentIndex,
        { showOffset }
    );

    // Update obstruction object
    obstruction.cords = ret.points;
    obstruction.polyShape = ret.shape;
    obstruction.mesh = ret.mesh;
    if (typeof ret.appliedOffset === "number") {
        obstruction.offset = ret.appliedOffset;
    }

    if (!showOffset) {
        const offsetLine = obstruction.mesh.getObjectByName("offset");
        const connectors = obstruction.mesh.getObjectByName("offsetConnectors");
        if (offsetLine) offsetLine.visible = false;
        if (connectors) connectors.visible = false;
    }

    // Keep it visually selected (yellow) if you want
    setObstacleVisualColor(obstruction.mesh, COLORS.yellow);
}

export function addExtrude(points, polyShape, height, color, scene, isFilled = false) {
    // Extrusion settings - no bevel for clean edges
    let extrudeSettings = {
        bevelEnabled: false,
    };

    // Create the basic extrude geometry from the 2D shape
    let extrudeGeometry = new THREE.ExtrudeGeometry(polyShape, extrudeSettings);
    
    // Small tolerance for floating point comparisons
    const tolerance = 0.0001;
    
    // Adjust the Z coordinates of the geometry vertices to match the 3D points
    // and apply the height to the top face
    for(let c = 0; c < extrudeGeometry.attributes.position.array.length; c += 3) {
        let x = extrudeGeometry.attributes.position.array[c];
        let y = extrudeGeometry.attributes.position.array[c+1];
        let z = extrudeGeometry.attributes.position.array[c+2];
        
        // Check each input point to see if this vertex matches its X/Y coordinates
        for (let cord in points) {
            // If the X/Y coordinates match (within tolerance)
            if(Math.abs(x - points[cord].x) < tolerance && Math.abs(y - points[cord].y) < tolerance) {
                // For bottom face vertices (z=0 in the extrude geometry)
                if(z === 0) {
                    // Set Z to match the point's Z coordinate
                    extrudeGeometry.attributes.position.array[c+2] = points[cord].z;
                }
                // For top face vertices (z=1 in the extrude geometry)
                else if(z === 1) {
                    // Set Z to be the point's Z coordinate plus the height
                    extrudeGeometry.attributes.position.array[c+2] = points[cord].z + height;
                }
            }
        }
    }

    // Mark the geometry as needing an update
    extrudeGeometry.attributes.position.needsUpdate = true;
    
    extrudeGeometry.computeVertexNormals();

    // Default obstacle body is wireframe-like (3D lines). It can be filled via UI toggle.
    const obstacleMaterial = new THREE.MeshPhongMaterial({
        color,
        transparent: true,
        opacity: isFilled ? 0.95 : 0,
        side: THREE.DoubleSide,
        shininess: isFilled ? 35 : 12,
        wireframe: false,
        depthWrite: isFilled,
    });
    const obstacleMesh = new THREE.Mesh(extrudeGeometry, obstacleMaterial);
    obstacleMesh.renderOrder = RENDER_LAYERS.OBSTACLE_OCCLUDER;

    // Draw only actual boundary edges (adjacent vertices), avoiding triangulation diagonals.
    if (!isFilled) {
        const edgeGeometry = new THREE.EdgesGeometry(extrudeGeometry, 1);
        const edgeMaterial = new THREE.LineBasicMaterial({
            color,
            linewidth: 4,
            transparent: true,
            opacity: 0.95,
            depthTest: false,
            depthWrite: false,
        });
        const edgeLines = new THREE.LineSegments(edgeGeometry, edgeMaterial);
        edgeLines.name = "obstacleBoundaryEdges";
        edgeLines.renderOrder = RENDER_LAYERS.OBSTACLE_OCCLUDER;
        obstacleMesh.add(edgeLines);
    }

    // Add the solid mesh to the scene (guarded by caller so scene should exist)
    scene.add(obstacleMesh);

    return obstacleMesh;
}

/**
 * Changes the height of an existing obstacle
 * 
 * This function updates the geometry of an obstacle to have a new height,
 * preserving its base position and shape.
 * 
 * @param {number} segmentIndex - Index of the segment containing the obstacle
 * @param {number} obstacleIndex - Index of the obstacle to modify
 * @param {number} height - New height for the obstacle in inches
 */
export function changeObstacleHeight(segmentIndex, obstacleIndex, height) {
    // Get the obstacle from the POLYGONS data structure
    const obstruction = POLYGONS[segmentIndex].obstructions[obstacleIndex];
    if (!obstruction) return;

    // Update height and rebuild geometry via the same solid-mesh pipeline.
    obstruction.height = height;
    updateObstacleShape(segmentIndex, obstacleIndex, obstruction.cords);

    // ✅ NEW: Propagate height changes to all linked segments
    if (obstruction.isFlushed && obstruction.linkedSegments && obstruction.linkedSegments.length > 1) {

        obstruction.linkedSegments.forEach(idx => {
            if (idx === segmentIndex) return;
            const seg = POLYGONS[idx];
            const sharedObs = seg.obstructions.find(o => o === obstruction);
            if (sharedObs) sharedObs.height = height;
        });
    }
}


/**
 * Changes the offset boundary of an existing obstacle
 * 
 * This function updates the offset boundary visualization around an obstacle,
 * which represents the safety margin that panels should not overlap with.
 * 
 * @param {number} segmentIndex - Index of the segment containing the obstacle
 * @param {number} obstacleIndex - Index of the obstacle to modify
 * @param {number} offset - New offset distance in inches
 */
export function changeObstacleOffset(segmentIndex, obstacleIndex, offset) {
    // Get the obstacle from the POLYGONS data structure
    const obstruction = POLYGONS[segmentIndex].obstructions[obstacleIndex];
    
    // Find the existing offset boundary by name
    const prevOffset = obstruction.mesh.getObjectByName("offset");
    const prevOffsetConnectors = obstruction.mesh.getObjectByName("offsetConnectors");

    // Remove and clean up the previous offset boundary if it exists
    if (prevOffset) {
        prevOffset.removeFromParent();
        prevOffset.geometry.dispose();
        prevOffset.material.dispose();
    }
    if (prevOffsetConnectors) {
        prevOffsetConnectors.removeFromParent();
        if (prevOffsetConnectors.geometry) prevOffsetConnectors.geometry.dispose();
        if (prevOffsetConnectors.material) prevOffsetConnectors.material.dispose();
    }
    
    // Create a new offset boundary with the new offset distance
    const {mesh, shape, points, appliedOffset} = addOffset(
        obstruction.mesh,
        obstruction.cords,
        obstruction.type,
        obstruction.polyShape,
        offset,
        obstruction.height,
        segmentIndex
    );
    
    // Update the obstacle properties with the new values
    obstruction.offset = (typeof appliedOffset === "number") ? appliedOffset : offset;
    obstruction.cords = points;
    obstruction.mesh = mesh;
    obstruction.polyShape = shape;
    
    // Set the color to yellow to indicate it's selected
    setObstacleVisualColor(obstruction.mesh, COLORS.yellow);

    // ✅ NEW: Propagate offset updates to all linked segments
    if (obstruction.isFlushed && obstruction.linkedSegments && obstruction.linkedSegments.length > 1) {

        obstruction.linkedSegments.forEach(idx => {
            if (idx === segmentIndex) return;
            const seg = POLYGONS[idx];
            const sharedObs = seg.obstructions.find(o => o === obstruction);
            if (sharedObs) sharedObs.offset = obstruction.offset;
        });
    }

    const affectedSegments =
        Array.isArray(obstruction.linkedSegments) && obstruction.linkedSegments.length
            ? obstruction.linkedSegments
            : [segmentIndex];
    refreshPanelOverlapHighlights(affectedSegments);

    return obstruction.offset;
}

export function changeObstacleFill(segmentIndex, obstacleIndex, isFilled) {
    const obstruction = POLYGONS[segmentIndex]?.obstructions?.[obstacleIndex];
    if (!obstruction) return;

    obstruction.isFilled = Boolean(isFilled);
    updateObstacleShape(segmentIndex, obstacleIndex, obstruction.cords);
}
